/*
  The OnHand logo. The only way any screen shows it.

  **Rebrand step 2, 17 September 2026: the OnHand brand's logo**
  (docs/brand/BRAND_GUIDE.md section 3, files in docs/brand/assets/logo/). It
  replaced the Phase 3 pack, which is kept as history in
  docs/design/phase-3/logo/.

  **Drawn inline from the brand files' own geometry**, src/components/brand/
  logoArt.js, which a test compares with those files shape by shape. Not a
  picture file, because the logo moves (guide 3.7) and a picture cannot. Not a
  redrawing either: every number is the file's.

  **The props did not change**, so no screen had to: `iconSize` still sizes the
  lockup, `layout` is still horizontal or stacked, `tone="white"` still asks
  for the version for a dark background. **The width a lockup takes is the
  width the old one took**, so every bar and card it sits in still fits (the
  old one was measured into those spaces). The brand's own sizes for the top
  bar (118 wide) and the sign-in card (210 wide) arrive with the steps that
  rebuild those.

  **Colour** (guide 3.3): the box and "On" are `--oh-logo-box` from the brand
  tokens, MedEx blue in light and white in dark, following data-color-mode; the
  item and "Hand" are MedEx gold always. Where no mode is set (the sign-in and
  opening screens) it is the primary blue version.

  **Motion** (guide 3.7, timings from docs/brand/reference/
  onhand-motion-approved-v1.html):
  - `animate="launch"`: the box draws in, the item drops in at 400ms, the letters
    rise at 650ms, settled by 1.3 seconds. **Once per page load**, on the first
    logo asked to do it that stays on screen long enough to finish.
  - `SyncIndicator`: the item falls into the box and sinks, every 1.9 seconds,
    with the word "Syncing", only while count numbers are waiting to be sent
    and the device has a connection. Hidden otherwise.
  - `animate="delivered"`: the success drop, the item falling into an empty box
    and settling by 700ms. Used in the message after a count is submitted or a
    client order is saved.
  **Nothing moves when a device asks for reduced motion**; the logo just
  appears.
*/
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  ICON_VIEWBOX, BOX_PATH, ITEM, ITEM_CLIP, STROKE, ON_RECT, ON_PATH, HAND_PATHS, HAND_CIRCLES,
  HORIZONTAL, STACKED, MEDEX_BLUE, MEDEX_GOLD, WHITE,
} from './logoArt';
import { subscribe as watchOutbox } from '../../lib/outbox';

// The brand's curves (guide 10.2), as the motion reference writes them.
const STANDARD = 'cubic-bezier(.2,0,0,1)';
const ENTER = 'cubic-bezier(.16,1,.3,1)';
const DROP = 'cubic-bezier(.45,0,.2,1)';

function reducedMotion() {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch { return false; }
}
const canAnimate = el => !!(el && typeof el.animate === 'function');

// The box, "On": blue on light, white on dark, white when asked for. A place
// whose ground is the opposite of the page's (the dark pop-up message on a light
// page) sets --logo-box-on-ground to pick the other version.
const boxColour = tone => (tone === 'white' || tone === 'onnavy' ? WHITE
  : `var(--logo-box-on-ground, var(--oh-logo-box, ${MEDEX_BLUE}))`);

const round = { fill: 'none', strokeWidth: STROKE, strokeLinecap: 'round', strokeLinejoin: 'round' };

/* The icon's parts. `refs` lets the motion reach the box and the item. */
function IconParts({ clipId, tone, refs, transform, emptyBox }) {
  return (
    <g transform={transform}>
      <defs>
        <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
          <rect x={ITEM_CLIP.x} y={ITEM_CLIP.y} width={ITEM_CLIP.width} height={ITEM_CLIP.height} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect ref={refs?.item} x={ITEM.x} y={ITEM.y} width={ITEM.width} height={ITEM.height} rx={ITEM.rx}
          transform={ITEM.transform} fill={MEDEX_GOLD}
          style={{ transformBox: 'fill-box', transformOrigin: '50% 100%', opacity: emptyBox ? 0 : undefined }} />
      </g>
      <path ref={refs?.box} d={BOX_PATH} pathLength="100" stroke={boxColour(tone)} {...round}
        style={{ transformBox: 'fill-box', transformOrigin: '50% 100%' }} />
    </g>
  );
}

function WordParts({ tone, refs, transform }) {
  return (
    <g transform={transform}>
      <g ref={refs?.on} stroke={boxColour(tone)} {...round}>
        <rect x={ON_RECT.x} y={ON_RECT.y} width={ON_RECT.width} height={ON_RECT.height} rx={ON_RECT.rx} />
        <path d={ON_PATH} />
      </g>
      <g ref={refs?.hand} stroke={MEDEX_GOLD} {...round}>
        {HAND_PATHS.map(d => <path key={d} d={d} />)}
        {HAND_CIRCLES.map(c => <circle key={c.cx} cx={c.cx} cy={c.cy} r={c.r} />)}
      </g>
    </g>
  );
}

// A clip id that is safe inside url(#...), unique per logo on the page. The
// brand files all use one id, which would collide with several logos inline.
function useClipId() {
  return 'oh-clip-' + useId().replace(/[^a-zA-Z0-9_-]/g, '');
}

/* -- Launch, once per page load --------------------------------------------- */
let launchPlayed = false;
let launchRunning = false;

function useLaunch(animate, refs) {
  useEffect(() => {
    if (animate !== 'launch' || launchPlayed || launchRunning || reducedMotion()) return undefined;
    const { box, item, on, hand } = refs;
    if (!canAnimate(box.current) || !canAnimate(item.current)) return undefined;
    launchRunning = true;
    const running = [
      box.current.animate([{ strokeDasharray: '100', strokeDashoffset: 100 }, { strokeDasharray: '100', strokeDashoffset: 0 }],
        { duration: 400, easing: STANDARD, fill: 'backwards' }),
      item.current.animate([{ translate: '0 -12px', opacity: 0 }, { translate: '0 -12px', opacity: 1, offset: 0.15 }, { translate: '0 2px', offset: 0.75 }, { translate: '0 0' }],
        { duration: 400, delay: 400, easing: ENTER, fill: 'backwards' }),
    ];
    if (canAnimate(on.current)) {
      running.push(on.current.animate([{ opacity: 0, translate: '0 8px' }, { opacity: 1, translate: '0 0' }],
        { duration: 350, delay: 650, easing: ENTER, fill: 'backwards' }));
    }
    if (canAnimate(hand.current)) {
      running.push(hand.current.animate([{ opacity: 0, translate: '0 8px' }, { opacity: 0.3, translate: '0 4px', offset: 0.3 }, { opacity: 1, translate: '0 0' }],
        { duration: 500, delay: 800, easing: ENTER, fill: 'backwards' }));
    }
    let finished = false;
    Promise.all(running.map(a => a.finished)).then(() => { finished = true; launchPlayed = true; launchRunning = false; }, () => {});
    return () => {
      if (!finished) { running.forEach(a => a.cancel()); launchRunning = false; }
    };
  }, [animate]); // eslint-disable-line
}

/* -- The success drop -------------------------------------------------------- */
function useDelivered(animate, refs) {
  useEffect(() => {
    if (animate !== 'delivered') return undefined;
    const { box, item } = refs;
    if (reducedMotion() || !canAnimate(item.current) || !canAnimate(box.current)) {
      if (item.current) item.current.style.opacity = '';
      return undefined;
    }
    item.current.style.opacity = '';
    const a = item.current.animate([{ translate: '0 -30px', opacity: 0 }, { translate: '0 -12px', opacity: 1, offset: 0.36 }, { translate: '0 1px', offset: 0.85 }, { translate: '0 0' }],
      { duration: 700, easing: DROP });
    const b = box.current.animate([{ scale: '1 1' }, { scale: '1 1', offset: 0.78 }, { scale: '1.03 0.95', offset: 0.86 }, { scale: '1 1' }],
      { duration: 700 });
    return () => { a.cancel(); b.cancel(); };
  }, [animate]); // eslint-disable-line
}

function useParts() {
  return { box: useRef(null), item: useRef(null), on: useRef(null), hand: useRef(null) };
}

function svgProps(decorative, className, style, width, height, viewBox) {
  return {
    viewBox, width, height, className,
    role: decorative ? undefined : 'img',
    'aria-label': decorative ? undefined : 'OnHand',
    'aria-hidden': decorative || undefined,
    focusable: 'false',
    style: { display: 'block', flex: '0 0 auto', overflow: 'visible', ...style },
  };
}

/* -- The three shapes ------------------------------------------------------- */

export function OnHandIcon({ size = 32, variant, tone, animate, decorative = false, className, style }) {
  const refs = useParts();
  const clipId = useClipId();
  useLaunch(animate, refs);
  useDelivered(animate, refs);
  const t = tone || (variant === 'reverse' || variant === 'white' ? 'white' : undefined);
  return (
    <svg {...svgProps(decorative, className, style, size, size, ICON_VIEWBOX)}>
      <IconParts clipId={clipId} tone={t} refs={refs} emptyBox={animate === 'delivered'} />
    </svg>
  );
}

export function OnHandLogo({ layout = 'horizontal', iconSize = 32, tone, animate, className, style }) {
  const refs = useParts();
  const clipId = useClipId();
  useLaunch(animate, refs);
  const spec = layout === 'stacked' ? STACKED : HORIZONTAL;
  // The same width the Phase 3 lockup took at this iconSize (its proportions
  // were 386.58 by 101, and 267.79 by 177.31 stacked), so nothing around it moves.
  const w = layout === 'stacked' ? Math.round(iconSize * 267.79 / 101) : Math.round(iconSize * 386.58 / 101);
  const h = Math.round(w * spec.h / spec.w);
  return (
    <svg {...svgProps(false, className, style, w, h, spec.viewBox)}>
      <IconParts clipId={clipId} tone={tone} refs={refs} transform={spec.icon} />
      <WordParts tone={tone} refs={refs} transform={spec.word} />
    </svg>
  );
}

/* The logo's box alone, as an outline in a muted colour: the mark of an empty
   state (guide 11.8). The brand's own box path, nothing added. */
export function OnHandBoxOutline({ size = 40, className, style }) {
  return (
    <svg {...svgProps(true, className, style, size, size, ICON_VIEWBOX)}>
      <path d={BOX_PATH} stroke="var(--text-muted)" {...round} />
    </svg>
  );
}

/* -- The syncing indicator ------------------------------------------------ */

/*
  **Only while OnHand is actually sending something.** The one thing OnHand
  sends in the background is count numbers that were typed with no signal
  (src/lib/outbox.js). So this shows while any are waiting AND the device says
  it has a connection, which is when the queue is being tried, and never
  otherwise. With no signal at all the count sheet's own bar says so instead.
  It reads the queue's state and changes nothing in it.
*/
export function useSyncing() {
  const [on, setOn] = useState(false);
  useEffect(() => watchOutbox(s => setOn(s.waiting > 0 && !s.offline)), []);
  return on;
}

function LoopIcon({ size = 22 }) {
  const refs = useParts();
  const clipId = useClipId();
  useEffect(() => {
    const { box, item } = refs;
    if (reducedMotion() || !canAnimate(item.current) || !canAnimate(box.current)) return undefined;
    const a = item.current.animate([{ translate: '0 -12px', opacity: 1 }, { translate: '0 0', opacity: 1, offset: 0.3 }, { translate: '0 0', opacity: 1, offset: 0.5 }, { translate: '0 4px', opacity: 0.35, offset: 0.85 }, { translate: '0 4px', opacity: 0 }],
      { duration: 1900, iterations: Infinity, easing: DROP });
    const b = box.current.animate([{ scale: '1 1' }, { scale: '1 1', offset: 0.28 }, { scale: '1.03 0.95', offset: 0.34 }, { scale: '1 1', offset: 0.45 }, { scale: '1 1' }],
      { duration: 1900, iterations: Infinity });
    return () => { a.cancel(); b.cancel(); };
  }, []); // eslint-disable-line
  return (
    <svg {...svgProps(true, 'oh-sync-mark', undefined, size, size, ICON_VIEWBOX)}>
      <IconParts clipId={clipId} refs={refs} />
    </svg>
  );
}

export function SyncIndicator({ showWord = true, tucked = false }) {
  const syncing = useSyncing();
  return (
    <span className={'oh-sync' + (tucked ? ' is-tucked' : '')} role="status" aria-live="polite">
      {syncing && (
        <>
          <LoopIcon />
          <span className={showWord ? 'oh-sync-word' : 'oh-visually-hidden'}>Syncing</span>
        </>
      )}
    </span>
  );
}

export default OnHandLogo;
